import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import TaskModal from './TaskModal';

const { FiPlus, FiSearch, FiCalendar, FiBriefcase, FiTrash2, FiEdit2, FiCheck, FiClock } = FiIcons;

const Tasks = () => {
  const { tasks, deleteTask, updateTask, projects } = useData();
  const [filter, setFilter] = useState('To Do');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const matchesStatus = filter === 'All' ? true : t.status === filter;
      const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    }).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  }, [tasks, filter, searchTerm]);

  const toggleTaskComplete = async (task) => {
    const newStatus = task.status === 'Completed' ? 'To Do' : 'Completed';
    await updateTask(task.id, { status: newStatus });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-5xl font-black text-slate-900 tracking-tighter">Tasks</h1>
          <p className="text-slate-400 font-medium text-lg">Central command for your daily operations.</p>
        </div>
        <button 
          onClick={() => { setEditingTask(null); setIsModalOpen(true); }}
          className="bg-blue-600 text-white px-10 py-5 rounded-[2rem] font-black shadow-2xl shadow-blue-100 flex items-center gap-3 hover:bg-blue-700 hover:-translate-y-1 transition-all"
        >
          <SafeIcon icon={FiPlus} /> New Task
        </button>
      </div>

      <div className="bg-white p-6 rounded-[3rem] border border-slate-100 shadow-sm flex flex-col md:flex-row gap-4 items-center">
        <div className="flex-1 relative w-full">
          <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 text-xl" />
          <input 
            placeholder="Search active tasks..." 
            className="w-full pl-16 pr-8 py-5 bg-slate-50 rounded-[2rem] outline-none focus:ring-4 focus:ring-blue-50 transition-all font-bold text-slate-700"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto p-1 bg-slate-50 rounded-[2rem]">
          {['To Do', 'In Progress', 'Completed', 'All'].map(s => (
            <button 
              key={s} 
              onClick={() => setFilter(s)}
              className={`flex-1 md:flex-none px-6 py-4 rounded-[1.5rem] font-black text-xs uppercase tracking-widest transition-all ${filter === s ? 'bg-white text-slate-900 shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode="popLayout">
          {filteredTasks.map(task => {
            const project = projects.find(p => p.id === task.projectId);
            return (
              <motion.div 
                layout 
                initial={{ opacity: 0, scale: 0.9 }} 
                animate={{ opacity: 1, scale: 1 }} 
                exit={{ opacity: 0, scale: 0.9 }}
                key={task.id}
                className={`bg-white p-10 rounded-[3.5rem] border border-slate-100 shadow-sm group hover:shadow-2xl hover:border-blue-100 transition-all relative overflow-hidden ${task.status === 'Completed' ? 'opacity-70 bg-slate-50/50' : ''}`}
              >
                <div className="flex justify-between items-start mb-8">
                  <button 
                    onClick={() => toggleTaskComplete(task)}
                    className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-all shadow-sm ${task.status === 'Completed' ? 'bg-green-500 text-white' : 'bg-slate-50 text-slate-300 hover:bg-blue-50 hover:text-blue-500'}`}
                  >
                    <SafeIcon icon={task.status === 'Completed' ? FiCheck : FiClock} className="text-2xl" />
                  </button>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={() => { setEditingTask(task); setIsModalOpen(true); }} className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button onClick={() => deleteTask(task.id)} className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>

                <h3 className={`text-2xl font-black text-slate-900 mb-3 leading-tight ${task.status === 'Completed' ? 'line-through text-slate-400' : ''}`}>{task.title}</h3>
                <p className="text-slate-400 text-sm font-medium line-clamp-2 mb-8">{task.description || 'No additional context.'}</p>
                
                <div className="space-y-4 pt-8 border-t border-slate-50">
                  <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    <div className="w-10 h-10 rounded-[1rem] bg-slate-50 flex items-center justify-center text-slate-400">
                      <SafeIcon icon={FiBriefcase} />
                    </div>
                    {project ? project.name : 'Independent Task'}
                  </div>
                  <div className="flex items-center gap-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                    <div className="w-10 h-10 rounded-[1rem] bg-slate-50 flex items-center justify-center text-slate-400">
                      <SafeIcon icon={FiCalendar} />
                    </div>
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : 'No Deadline'}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <TaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} task={editingTask} />
    </div>
  );
};

export default Tasks;